// Outcome-named journey recipes: the front door of the journeys feature.
// Each ships a trigger, steps with timing, and copy, ready to switch on
// after a quick read-through. Email steps start with a subject and simple
// starter blocks; the full design happens in the email designer.

const id = (() => { let n = 0; return (p) => `${p}-${++n}`; })();

const starterEmailBlocks = (headline, body, buttonText) => [
  { id: id('b'), type: 'layout-editorial-hero', content: {
    headline, subtext: body, backgroundColor: '#ffffff', textColor: '#161616',
    headlineFontFamily: 'Georgia, serif', headlineSize: 40,
  }},
  { id: id('b'), type: 'button', content: {
    text: buttonText, url: '{{BookingLink}}', shape: 'pill', uppercase: true,
    fontSize: 12, buttonColor: '#29282D', textColor: '#ffffff', align: 'center',
  }},
  { id: id('b'), type: 'footer', content: { text: `© ${new Date().getFullYear()} {{SalonName}}. All rights reserved.` } },
];

export const JOURNEY_RECIPES = [
  {
    id: 'welcome-series',
    name: 'New client welcome',
    popular: true,
    description: 'Welcome a first-time client, then nudge them to rebook if they have not been back. The journey ends early the moment they book.',
    journey: {
      name: 'New client welcome',
      status: 'paused',
      trigger: { type: 'new-client' },
      goalExit: true,
      steps: [
        { id: id('s'), type: 'email', subject: 'Lovely to meet you, {{FirstName}}', blocks: starterEmailBlocks('Welcome to\n{{SalonName}}', 'Thank you for choosing us for your first visit. Here is everything worth knowing, and we would love to see you again.', 'Book your next visit') },
        { id: id('s'), type: 'wait', days: 14 },
        { id: id('s'), type: 'sms', message: 'Hi {{FirstName}}, it was lovely having you at {{SalonName}}. Fancy your next visit? Book here: {{BookingLink}} Reply STOP to opt out' },
      ],
    },
  },
  {
    id: 'rebook-nudge',
    name: 'Rebook nudge',
    description: 'Three weeks after a visit, remind clients with nothing booked to grab their next slot.',
    journey: {
      name: 'Rebook nudge',
      status: 'paused',
      trigger: { type: 'appointment-completed' },
      goalExit: true,
      steps: [
        { id: id('s'), type: 'wait', days: 21 },
        { id: id('s'), type: 'filter', condition: 'no-future-booking' },
        { id: id('s'), type: 'email', subject: 'Time for your next visit, {{FirstName}}?', blocks: starterEmailBlocks('Ready when\nyou are', 'It has been a few weeks since your last visit. Your favourite slot is probably free right now.', 'Rebook in seconds') },
      ],
    },
  },
  {
    id: 'win-back',
    name: 'Win back quiet clients',
    description: 'A warm email when a client goes quiet, with a text a week later if they still have not booked.',
    journey: {
      name: 'Win back quiet clients',
      status: 'paused',
      trigger: { type: 'lapsed', lapsedWeeks: 8 },
      goalExit: true,
      steps: [
        { id: id('s'), type: 'email', subject: 'It’s been a while, {{FirstName}}', blocks: starterEmailBlocks('It’s been\na while', 'No guilt, just a fresh start. We would love to see you back soon.', 'Pick up where we left off') },
        { id: id('s'), type: 'wait', days: 7 },
        { id: id('s'), type: 'sms', message: 'Hi {{FirstName}}, we miss you at {{SalonName}}! Book your comeback visit here: {{BookingLink}} Reply STOP to opt out' },
      ],
    },
  },
  {
    id: 'birthday-treat',
    name: 'Birthday treat',
    description: 'A personal birthday email on the morning, with a treat to bring them in during their birthday month.',
    journey: {
      name: 'Birthday treat',
      status: 'paused',
      trigger: { type: 'birthday' },
      goalExit: true,
      steps: [
        { id: id('s'), type: 'email', subject: 'Happy birthday, {{FirstName}}', blocks: starterEmailBlocks('Happy\nBirthday!', 'Happy birthday from all of us. Why not treat yourself this month? You deserve it.', 'Book a birthday visit') },
      ],
    },
  },
  {
    id: 'aftercare',
    name: 'Post-visit aftercare',
    description: 'The day after a visit, send care tips that keep results lasting and clients feeling looked after.',
    journey: {
      name: 'Post-visit aftercare',
      status: 'paused',
      trigger: { type: 'appointment-completed' },
      goalExit: false,
      steps: [
        { id: id('s'), type: 'wait', days: 1 },
        { id: id('s'), type: 'email', subject: 'Caring for your new look', blocks: starterEmailBlocks('Keep the\nfeeling going', 'A few simple tips to keep yesterday’s results looking their best until we see you next.', 'Book your next visit') },
      ],
    },
  },
  {
    id: 'first-visit-followup',
    name: 'First visit follow-up',
    description: 'A quick how-was-it text the day after a first appointment. Small touch, big loyalty.',
    journey: {
      name: 'First visit follow-up',
      status: 'paused',
      trigger: { type: 'new-client' },
      goalExit: false,
      steps: [
        { id: id('s'), type: 'wait', days: 1 },
        { id: id('s'), type: 'sms', message: 'Hi {{FirstName}}, thanks for visiting {{SalonName}} yesterday! We hope you love the result. We are always happy to hear how it went. Reply STOP to opt out' },
      ],
    },
  },
  {
    id: 'no-show-recovery',
    name: 'No-show recovery',
    description: 'A gentle, no-guilt text the day after a missed appointment. Recovers the booking without souring the relationship.',
    journey: {
      name: 'No-show recovery',
      status: 'paused',
      trigger: { type: 'no-show' },
      goalExit: true,
      steps: [
        { id: id('s'), type: 'wait', days: 1 },
        { id: id('s'), type: 'sms', message: 'Hi {{FirstName}}, we missed you yesterday! Life happens. Whenever you are ready, your slot is waiting: {{BookingLink}} Reply STOP to opt out' },
      ],
    },
  },
];
